/**
 * @jsxRuntime classic
 * @jsx jsx
 * @jsxFrag Fragment
 */
import { type ComponentPropsWithoutRef, Fragment, Suspense, lazy, memo } from 'react';

import { cssMap as compiledCssMap, jsx } from '@compiled/react';
import { type MessageDescriptor, useIntl } from 'react-intl';

import { IconButton } from '@atlaskit/button/new';
import { cssMap } from '@atlaskit/css';
import FeatureGates from '@atlaskit/feature-gate-js-client';
import CrossIcon from '@atlaskit/icon/core/cross';
import LocationIcon from '@atlaskit/icon/core/location';
import UndoIcon from '@atlaskit/icon/core/undo';
import Link from '@atlaskit/link';
import { Box, Inline, Text } from '@atlaskit/primitives/compiled';
import type { Card as TSmartCard } from '@atlaskit/smart-card';
import { token } from '@atlaskit/tokens';
import { type ContentContext } from '@atlassian/conversation-assistant-store/controllers/chat-context/store';

const LazySmartCard = lazy(() =>
	import(
		/* webpackChunkName: "@atlassian/conversation-assistant_smart-card" */ '@atlaskit/smart-card'
	).then((m) => ({ default: m.Card })),
);
const SmartCard = (props: ComponentPropsWithoutRef<typeof TSmartCard>) => (
	<Suspense fallback={null}>
		<LazySmartCard {...props} />
	</Suspense>
);

import { type AiFeatureContextOverride, type BrowserContextSanitizer } from '../../../types';

import { messages } from './messages';
import { type ContextInfo, useActiveContext } from './useActiveContext';

const styles = cssMap({
	bannerWrapper: {
		paddingInline: token('space.150'),
		paddingBlock: token('space.100'),
		borderRadius: token('radius.xlarge'),
		backgroundColor: token('elevation.surface.sunken'),
		marginBlockEnd: token('space.150'),
		minHeight: '40px',
	},
	contentContainer: {
		maxWidth: '100%',
		overflow: 'hidden',
	},
	contextLabel: {
		flexShrink: 0,
	},
	fallbackLink: {
		overflow: 'hidden',
		textOverflow: 'ellipsis',
		whiteSpace: 'nowrap',
	},
	// TODO: add a proper icon for the referencing document
	referencingDocumentIcon: {
		transform: 'rotate(180deg)',
	},
});

const unsafelyCompiledCssMap = compiledCssMap({
	smartCard: {
		maxWidth: '100%',
		// eslint-disable-next-line @atlaskit/design-system/no-nested-styles, @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
		'& > .loader-wrapper': {
			display: 'flex',
		},
	},
});

type ContextLabelType = 'context' | 'referencing_text' | 'referencing_issues';

interface ContextBannerItemProps {
	contextInfo: ContextInfo;
}

const DATABASE_CONTENT_LABELS: Partial<Record<string, MessageDescriptor>> = {
	cell: messages.referencingSelectedCellsLabel,
	field: messages.referencingSelectedFieldsLabel,
	card: messages.referencingSelectedCardsLabel,
	entry: messages.referencingSelectedEntriesLabel,
};

const getReferencingDatabaseContentLabel = (selectedDatabaseContent: string[]) => {
	if (selectedDatabaseContent.length === 0) {
		return { descriptor: messages.referencingDatabaseViewLabel };
	}
	// extract the prefix from the first selected database content
	const prefix = selectedDatabaseContent[0].match(/^(cell|field|card|entry)/)?.[1];
	const descriptor = prefix
		? DATABASE_CONTENT_LABELS[prefix]
		: messages.referencingDatabaseViewLabel;
	return {
		descriptor: descriptor ?? messages.referencingDatabaseViewLabel,
		count: prefix ? selectedDatabaseContent.length : undefined,
	};
};

const getReferencingLabelText = (
	formatMessage: (descriptor: MessageDescriptor, values?: Record<string, number>) => string,
	selectedDatabaseContent: string[] | undefined,
): string => {
	if (!selectedDatabaseContent) {
		return formatMessage(messages.referencingDocumentLabel);
	}
	const { descriptor, count } = getReferencingDatabaseContentLabel(selectedDatabaseContent);
	return count !== undefined ? formatMessage(descriptor, { count }) : formatMessage(descriptor);
};

const getContextLabelContent = (
	formatMessage: (descriptor: MessageDescriptor, values?: Record<string, number>) => string,
	labelType: ContextLabelType,
	selectedDatabaseContent: string[] | undefined,
	selectedIssueCount: number | undefined,
): string => {
	if (labelType === 'referencing_text') {
		return getReferencingLabelText(formatMessage, selectedDatabaseContent);
	}
	if (labelType === 'referencing_issues' && selectedIssueCount !== undefined) {
		return formatMessage(messages.referencingIssuesLabel, { count: selectedIssueCount });
	}
	return '';
};

const ContextBannerItem = ({ contextInfo }: ContextBannerItemProps) => {
	const { formatMessage } = useIntl();
	const currentUrl = contextInfo.url;
	const currentTitle = contextInfo.title;
	const { labelType, selectedDatabaseContent, selectedIssueCount } = contextInfo;

	return (
		<Inline spread="space-between" alignBlock="center" space="space.100">
			<Inline xcss={styles.contentContainer} space="space.050" alignBlock="center">
				<Inline xcss={styles.contextLabel} space="space.050" alignBlock="center">
					{labelType === 'referencing_text' || labelType === 'referencing_issues' ? (
						<Fragment>
							<Inline xcss={styles.referencingDocumentIcon} space="space.050">
								<UndoIcon size="small" label="" color={token('color.text.subtlest')} />
							</Inline>
							<Text size="small" color="color.text.subtlest">
								{getContextLabelContent(
									formatMessage,
									labelType,
									selectedDatabaseContent,
									selectedIssueCount,
								)}
							</Text>
						</Fragment>
					) : (
						<Fragment>
							<LocationIcon size="small" label="" color={token('color.text.subtlest')} />
							<Text size="small" color="color.text.subtlest">
								{formatMessage(messages.contextBannerLabel)}:
							</Text>
						</Fragment>
					)}
				</Inline>

				<span css={unsafelyCompiledCssMap.smartCard}>
					<SmartCard
						key={currentUrl}
						url={currentUrl}
						appearance="inline"
						truncateInline
						resolvingPlaceholder={currentTitle}
						onClick={() => {
							window.open(currentUrl, '_blank');
						}}
						fallbackComponent={() => (
							<Box xcss={styles.fallbackLink}>
								<Link
									href={currentUrl}
									rel="noopener noreferrer"
									target="_blank"
									appearance="subtle"
								>
									{currentTitle}
								</Link>
							</Box>
						)}
					/>
				</span>
			</Inline>

			<IconButton
				spacing="compact"
				isTooltipDisabled={false}
				appearance="subtle"
				shape="circle"
				label={formatMessage(messages.removeContextButtonLabel)}
				icon={() => <CrossIcon size="small" label="" color={token('color.text.subtlest')} />}
				color="color.text.subtlest"
				onClick={contextInfo.onDismiss}
			/>
		</Inline>
	);
};

export interface ContextBannerProps {
	contentContext?: ContentContext;
	currentPageAsContextStoreKey: string;
	enableBrowserContextBanner?: boolean;
	browserContextSanitizer?: BrowserContextSanitizer;
	aiFeatureContextOverride?: AiFeatureContextOverride;
	/**
	 * Invoked after the user removes the active context via the "Remove context"
	 * button. Used to move focus back to the prompt input so keyboard and screen
	 * reader users are not stranded when the banner unmounts.
	 */
	onContextRemoved?: () => void;
}

const ContextBannerComponent = (props: ContextBannerProps): JSX.Element | null => {
	const activeContext = useActiveContext(props);

	if (!activeContext) {
		return null;
	}

	return (
		<Box xcss={styles.bannerWrapper}>
			<ContextBannerItem contextInfo={activeContext} />
		</Box>
	);
};

// Reference-identity comparison of all `ContextBannerProps`. This is equivalent
// to React's default shallow compare over these props; it is spelled out so the
// freshness-input contract is explicit. Two of these props (`browserContextSanitizer`
// and `aiFeatureContextOverride`) are objects compared by identity, so callers MUST
// pass referentially stable values for them — constructing either inline at the call
// site would defeat this memo (stale UI or render thrash). If a new prop is added to
// `ContextBannerProps`, it must be added here too, otherwise it will be silently
// ignored by the memo.
const areContextBannerPropsEqual = (
	previousProps: ContextBannerProps,
	nextProps: ContextBannerProps,
): boolean =>
	Object.is(previousProps.contentContext, nextProps.contentContext) &&
	Object.is(previousProps.currentPageAsContextStoreKey, nextProps.currentPageAsContextStoreKey) &&
	Object.is(previousProps.enableBrowserContextBanner, nextProps.enableBrowserContextBanner) &&
	Object.is(previousProps.browserContextSanitizer, nextProps.browserContextSanitizer) &&
	Object.is(previousProps.aiFeatureContextOverride, nextProps.aiFeatureContextOverride) &&
	Object.is(previousProps.onContextRemoved, nextProps.onContextRemoved);

const MemoizedContextBanner = memo(ContextBannerComponent, areContextBannerPropsEqual);

export const ContextBanner = (props: ContextBannerProps): JSX.Element | null => {
	// eslint-disable-next-line @atlaskit/platform/use-recommended-utils
	const gateEnabled = FeatureGates.getExperimentValue(
		'rovo_ttai_segment_render_stabilization',
		'isEnabled',
		false,
	);
	return gateEnabled ? <MemoizedContextBanner {...props} /> : <ContextBannerComponent {...props} />;
};
